import { InfoCircleOutlined, TableOutlined } from '@ant-design/icons';
import { FC, useState } from 'react';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsMetricDetailModal, {
  IOpsMetricDetailConfig,
} from '../OpsMetricDetailModal';

type SglStatus = 'up' | 'warn' | 'down';

interface IOpsVolumeActivityCardsProps {
  locationId: string;
  periodLabel: string;
  recruited: number;
  dblCount: number;
  sglCount: number;
  sglPercent?: number;
  unitsPerDay: number;
  prevUnitsPerDay?: number;
  successRate?: number;
  attemptedDonations?: number;
  successfulDonations?: number;
  totalUnits?: number;
  donationEventCount?: number;
  onDrilldown?: (metricKey: string) => void;
}

function getSglStatus(sglPercent?: number): SglStatus {
  if (sglPercent == null) return 'up';
  if (sglPercent >= 28) return 'down';
  if (sglPercent >= 23) return 'warn';
  return 'up';
}

const OpsVolumeActivityCards: FC<IOpsVolumeActivityCardsProps> = ({
  locationId,
  periodLabel,
  recruited,
  dblCount,
  sglCount,
  sglPercent,
  unitsPerDay,
  prevUnitsPerDay,
  successRate,
  attemptedDonations,
  successfulDonations,
  totalUnits,
  donationEventCount,
  onDrilldown,
}) => {
  const { isDarkMode } = useOperationsDashboardContext();
  const [modalConfig, setModalConfig] = useState<IOpsMetricDetailConfig | null>(
    null
  );

  const sglStatus = getSglStatus(sglPercent);

  // Units/day trend
  const updTrendPct =
    prevUnitsPerDay && prevUnitsPerDay > 0
      ? Math.round(((unitsPerDay - prevUnitsPerDay) / prevUnitsPerDay) * 100)
      : undefined;
  const updTrendUp = updTrendPct != null && updTrendPct >= 0;

  // Pill styling based on SGL status
  const dblPillClass =
    sglStatus === 'up'
      ? styles.volPillActive
      : sglStatus === 'warn'
      ? styles.volPillWarn
      : styles.volPillMuted;
  const sglPillClass =
    sglStatus === 'down'
      ? styles.volPillDown
      : sglStatus === 'warn'
      ? styles.volPillWarn
      : styles.volPillMuted;

  return (
    <>
      <div className={styles.volCardsRow}>
        {/* Recruited */}
        <div className={styles.volCard}>
          <span className={styles.volCardLabel}>Recruited ({periodLabel})</span>
          <div className={styles.healthFlagCardValueRow}>
            <span className={styles.volCardValue}>{recruited}</span>
            <button
              type="button"
              className={styles.healthFlagDrilldownBtn}
              data-drilldown-id={`${locationId}:recruited`}
              title="View in Drilldown"
              onClick={(e) => {
                e.stopPropagation();
                onDrilldown?.('recruited');
              }}
            >
              <TableOutlined />
            </button>
          </div>
          <div className={styles.volPills}>
            <span className={`${styles.volPill} ${dblPillClass}`}>
              DBL {dblCount}
            </span>
            <span className={`${styles.volPill} ${sglPillClass}`}>
              SGL {sglCount}
            </span>
          </div>
        </div>

        {/* Units / Event */}
        <div className={`${styles.volCard} ${styles.withInfoTab}`}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) => {
              e.stopPropagation();
              setModalConfig({
                title: 'Units / Event',
                description: (
                  <>
                    <strong>Units / Event</strong> is the average number of pRBC
                    units produced per donation event at this location during
                    the selected period ({periodLabel}). Higher values indicate
                    a strong DBL donor mix and efficient donation sessions.
                  </>
                ),
                rows: [
                  {
                    label: 'Units / Event',
                    value: unitsPerDay.toFixed(1),
                    bold: true,
                    color: '#4B9EFF',
                  },
                  ...(prevUnitsPerDay !== undefined
                    ? [
                        {
                          label: `Prior period units / event`,
                          value: prevUnitsPerDay.toFixed(1),
                        },
                        ...(updTrendPct !== undefined
                          ? [
                              {
                                label: 'Period-over-period',
                                value: `${
                                  updTrendPct > 0 ? '+' : ''
                                }${updTrendPct}%`,
                                bold: true,
                                color: updTrendUp ? '#00E5A0' : '#E8008A',
                              },
                            ]
                          : []),
                      ]
                    : []),
                ],
                formula:
                  'Units/Event = Total Units Collected ÷ Number of Donation Events',
              });
            }}
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.volCardLabel}>Units/Event</span>
          <span className={styles.volCardValue}>{unitsPerDay.toFixed(1)}</span>
          {updTrendPct != null && (
            <span
              className={`${styles.volCardTrend} ${
                updTrendUp ? styles.volCardTrendUp : styles.volCardTrendDown
              }`}
            >
              {updTrendUp ? '\u2191' : '\u2193'} {Math.abs(updTrendPct)}%
            </span>
          )}
        </div>

        {/* Collection Rate */}
        <div className={`${styles.volCard} ${styles.withInfoTab}`}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) => {
              e.stopPropagation();
              setModalConfig({
                title: 'Donation Success Rate',
                description: (
                  <>
                    <strong>Donation Success Rate</strong> measures the
                    percentage of attempted donations that successfully yielded
                    at least one non-discarded product (pRBC or FFP). Ineligible
                    donations are excluded from both attempted and successful
                    counts. A donation is successful if its status is Complete,
                    DBL→SGL, or Partial.
                  </>
                ),
                statusLabel:
                  successRate != null
                    ? successRate >= 70
                      ? 'Good'
                      : successRate >= 50
                      ? 'OK'
                      : 'Low'
                    : undefined,
                rows: [
                  {
                    label: 'Donation Success Rate',
                    value: successRate != null ? `${successRate}%` : '—',
                    bold: true,
                    color: '#E8008A',
                  },
                  {
                    label: 'Successful Donations',
                    value: successfulDonations ?? '—',
                    bold: true,
                  },
                  {
                    label: 'Attempted Donations',
                    value: attemptedDonations ?? '—',
                  },
                ],
                formula:
                  'Success Rate = Successful Donations ÷ Attempted Donations × 100 (excludes ineligible)',
              });
            }}
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.volCardLabel}>Success Rate</span>
          <span className={styles.volCardValue}>
            {successRate != null ? `${successRate}%` : '—'}
          </span>
        </div>
      </div>

      <OpsMetricDetailModal
        visible={modalConfig !== null}
        onClose={() => setModalConfig(null)}
        config={modalConfig}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default OpsVolumeActivityCards;
